import {
  useCallback, useEffect, useMemo, useState
} from "react";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { Grid, MenuProps } from "antd";
import { SelectInfo } from "rc-menu/lib/interface";
import useCurrentUser from "../../hooks/useCurrentUser";
import {
  UserDataType, UseSchedulerProps, ScheduleAvailabilitiesType, TimeType,
  DoctorData
} from "../../types/scheduler";
import { useAppDispatch, useAppSelector } from "../../store";
import { fetchSchedulesData, isSchedulesLoading, resetSchedulerData, schedulesData } from "../../store/slices/scheduler";
import { DATE_FORMAT } from "../../constants/date";

const { useBreakpoint } = Grid;

const useScheduler = (props: UseSchedulerProps & { doctorData?: DoctorData }) => {
  const {
    defaultDay = dayjs(), // get today's day by default
    defaultDays = 5,
    doctorData
  } = props;

  const dispatch = useAppDispatch();

  const { role, isDoctor } = useCurrentUser();
  const screens = useBreakpoint();
  const [
    userData,
    setUserData
  ] = useState<UserDataType | null>(null);

  const schedulerData = useAppSelector(schedulesData);
  const isSchedulerLoading = useAppSelector(isSchedulesLoading);
  const [
    selectedSchedules,
    setSelectedSchedules
  ] = useState<TimeType | null>(null);

  useEffect(() => {
    dispatch(fetchSchedulesData(doctorData ? { doctorData } : {}));
  }, [doctorData]);

  useEffect(() => {
    return () => {
      dispatch(resetSchedulerData());
    };
  }, []);

  useEffect(() => {
    if (!schedulerData) {
      return;
    }

    if (doctorData?.role) {
      setUserData(schedulerData[doctorData.role]);
      return;
    }

    if (isDoctor) {
      setUserData(schedulerData[role]);
    } else {
      setUserData(
        schedulerData[role].oncologist?.at(-1) ?? schedulerData[role].nutrizionist?.at(-1)
      );
    }
  }, [schedulerData]);

  const [startDate, setStartDate] = useState(defaultDay);
  const [currentMonth, setCurrentMonth] = useState<string>(defaultDay.format("MMMM"));

  const days = useMemo(() => {
    return screens.md ? defaultDays : 2;
  }, [screens]);

  useEffect(() => {
    setCurrentMonth(startDate.format("MMMM"));
  }, [startDate]);

  const formatTimeRange = useCallback((
    currentDate: Dayjs,
    timeRange: ScheduleAvailabilitiesType[]
  ): TimeType[] => {
    if (!userData) {
      return [];
    }

    const currentAppointments = userData.appointments.filter(
      (appointmentItem) => {
        return currentDate.isSame(dayjs(appointmentItem.start_at), "day") && appointmentItem.is_paid;
      }
    );

    const appointments = currentAppointments.map(currentAppointmentItem => {
      const isPaid = currentAppointmentItem.is_paid;
      const startTime = dayjs(currentAppointmentItem.start_at);
      const timeFrom = `${startTime.hour().toString().padStart(2, "0")}:00`;

      return {
        id: startTime.hour(),
        from: timeFrom,
        time: `${timeFrom} - ${startTime.add(1, "hour").hour().toString()}:00`,
        current: dayjs(currentDate).format(DATE_FORMAT.FULL_DATE),
        appointment: currentAppointmentItem,
        disabled: isDoctor ? !isPaid : isPaid
      };
    });

    return timeRange.reduce((acc: TimeType[], item: ScheduleAvailabilitiesType) => {
      const timeFrom = parseInt(item.time_from.split(":")[0], 10);
      const timeTo = parseInt(item.time_to.split(":")[0], 10);
      const currentDay = dayjs(currentDate).format(DATE_FORMAT.FULL_DATE);

      for (let i = timeFrom; i < timeTo; i += 1) {
        const startHour = `${i.toString().padStart(2, "0")}:00`;
        const endHour = `${(i + 1).toString().padStart(2, "0")}:00`;
        const appointment = currentAppointments.find(
          (appointmentItem) => {
            return dayjs(`${currentDay} ${startHour}:00`).isSame(dayjs(appointmentItem.start_at));
          }
        );

        const isBlocked = userData.blockedPeriods.some(period => {
          const from = dayjs(period.from);
          const to = dayjs(period.to);
          const startTime = dayjs(`${currentDay} ${startHour}:00`);
          const endTime = dayjs(`${currentDay} ${endHour}:00`);
          return startTime.isBetween(from, to, "day", "[]")
          && endTime.isBetween(from, to, "day", "[]");
        });

        const isPaid = appointment ? appointment.is_paid : false;
        const isExisting = acc.some(timeItem => timeItem.from === startHour);

        const isDisabled = isDoctor
          ? !isPaid
          : (
            isBlocked
            || isPaid
            || dayjs(currentDate).isSame(dayjs(), "day")
            || dayjs(currentDate).isSame(dayjs().add(1, "day"), "day")
          );

        if (!isExisting) {
          acc.push({
            id: i,
            from: startHour,
            time: `${startHour} - ${endHour}`,
            current: currentDay,
            appointment,
            disabled: isDisabled
          });
        }
      }

      return acc;
    }, appointments).sort((a, b) => {
      return a.id > b.id ? 1 : -1;
    });
  }, [userData]);

  const isCurrentWeek = useMemo(() => {
    return startDate.diff(defaultDay, "hours") <= 0;
  }, [startDate, defaultDay]);

  const daysRange = useMemo(() => {
    const result = [];
    let current = startDate;

    for (let i = 1; days + 1 !== i; i += 1) {
      const dayString = current.locale("en").format("dddd");

      result.push({
        date: current,
        day: dayString,
        schedules: [] as TimeType[]
      });

      current = current.add(1, "day");
    }
    return result;
  }, [startDate, days]);

  const currentRange = useMemo(() => {
    let range = daysRange;
    if (userData) {
      const { availabilities } = userData;

      range = daysRange.map((item) => {
        const schedules = availabilities.filter(
          (availabilityItem: any) => {
            return availabilityItem.day === item.day;
          }
        );

        item.schedules = formatTimeRange(item.date, schedules);

        return item;
      });
    }

    return range;
  }, [userData, daysRange, isDoctor]);

  const handleNext = () => {
    setStartDate((prevState) => {
      return prevState.add(days, "day");
    });
  };

  const handlePrev = () => {
    setStartDate((prevState) => {
      const newDate = prevState.add(-days, "day");

      if (newDate.diff(defaultDay, "hours") < 0) {
        return defaultDay;
      }

      return newDate;
    });
  };

  const items: MenuProps["items"] = useMemo(() => {
    const array = [];

    for (let i = 0; array.length <= 7; i += 1) {
      const month = dayjs().startOf("month").add(i, "month");
      array.push({
        key: i,
        label: month.format(DATE_FORMAT.FULL_MONTH)
      });
    }

    return array;
  }, []);

  const handleOnSelect = (menuItem: SelectInfo) => {
    let newDate = defaultDay.startOf("month").add(Number(menuItem.key), "month");
    if (newDate.diff(defaultDay, "hours") < 0) {
      newDate = defaultDay;
    }

    setStartDate(newDate);
  };

  return {
    items,
    setStartDate,
    currentMonth,
    handlePrev,
    handleNext,
    currentRange,
    handleOnSelect,
    isCurrentWeek,
    days,
    isSchedulerLoading,
    selectedSchedules,
    setSelectedSchedules
  };
};

export default useScheduler;
